import * as Sentry from '@sentry/react';
import type { BrowserOptions } from '@sentry/react';

const SENTRY_DSN = 'https://b6e0e56681874ef08814fe61c2855c74@sentry.meitu-int.com/227';
const FULL_SENTRY_TRACE_SAMPLE_RATE = 1.0;
const DEFAULT_SENTRY_TRACE_SAMPLE_RATE = 0.1;

type SentryTraceSamplingContext = Parameters<NonNullable<BrowserOptions['tracesSampler']>>[0];

/**
 * 核心 Web Vitals 与用户交互保持全量，路由和普通 Trace 按 10% 采样并延续上游决定。
 */
export function resolveSentryTraceSampleRate({
  attributes,
  parentSampled,
}: SentryTraceSamplingContext): number | boolean {
  const operation = attributes?.['sentry.op'];

  if (
    operation === 'pageload'
    || (typeof operation === 'string' && operation.startsWith('ui.interaction'))
  ) {
    return FULL_SENTRY_TRACE_SAMPLE_RATE;
  }

  if (operation === 'navigation') {
    return DEFAULT_SENTRY_TRACE_SAMPLE_RATE;
  }

  if (typeof parentSampled === 'boolean') {
    return parentSampled;
  }

  return DEFAULT_SENTRY_TRACE_SAMPLE_RATE;
}

/**
 * 在浏览器公共入口尽早且仅初始化一次 Sentry，采集首屏及路由活动期的 fetch/XHR 性能 Span。
 */
export function initBrowserSentry(): void {
  if (
    typeof window === 'undefined'
    || !process.env.NEXT_PUBLIC_SENTRY_AUTH_TOKEN
    || Sentry.isInitialized()
  ) {
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.NEXT_PUBLIC_ENVIRONMENT,
    release: `roboneo-web@${process.env.NEXT_PUBLIC_ENVIRONMENT}-${Sentry.SDK_VERSION}`,
    integrations: [
      Sentry.browserTracingIntegration({
        traceFetch: true,
        traceXHR: true,
        enableHTTPTimings: true,
      }),
    ],
    normalizeDepth: 10,
    maxValueLength: 5000,
    maxBreadcrumbs: 100,
    tracesSampler: resolveSentryTraceSampleRate,
  });
}
